import { flexRender, type Table as TableType } from "@tanstack/react-table";
import clsx from "clsx";

import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import Checkbox from "common/components/ui/Checkbox";
import Skeleton from "common/components/ui/Skeleton";
import {
  Table,
  TableCell,
  TableHead,
  TableRow,
} from "common/components/ui/TableV2";
import { Tag } from "common/components/ui/Tag";
import { formatNumber } from "common/helpers/utils";

import type { ReactNode } from "react";

interface BasicDataTableProps<TData extends { id: string }> {
  table: TableType<TData>;
  onRowClick?: (rowValue: TData) => void;
  totalItemCount: number;
  isLoading: boolean;
  selectionActionsComponent?: ReactNode;
}

export default function DataTable<TData extends { id: string }>({
  onRowClick,
  table,
  totalItemCount,
  isLoading,
  selectionActionsComponent,
}: BasicDataTableProps<TData>) {
  const {
    getHeaderGroups,
    getRowModel,
    getIsAllPageRowsSelected,
    getIsAllRowsSelected,
    getIsSomePageRowsSelected,
    getIsSomeRowsSelected,
    toggleAllPageRowsSelected,
    toggleAllRowsSelected,
  } = table;

  const isRowSelectionEnabled = table.options.enableRowSelection !== false;

  let selectAllCheckboxState: "indeterminate" | boolean =
    getIsAllPageRowsSelected();

  if (getIsSomePageRowsSelected()) {
    selectAllCheckboxState = "indeterminate";
  }

  const canInteractWithRows = !isLoading && Boolean(onRowClick);
  const selectedRowsCount = Object.keys(table.getState().rowSelection).length;
  const hasSelectedRows = selectedRowsCount > 0;

  let selectionActionButton: JSX.Element;

  if (
    isRowSelectionEnabled &&
    hasSelectedRows &&
    !table.options.manualPagination
  ) {
    const isSelectAll = getIsSomeRowsSelected() && !getIsAllRowsSelected();

    selectionActionButton = (
      <Button
        size="sm"
        variant="link-purple"
        onClick={() => {
          toggleAllRowsSelected(isSelectAll);
        }}
      >
        {isSelectAll ? "Select all" : "Clear selection"}
      </Button>
    );
  }

  return (
    <div className="relative">
      <Table>
        <thead>
          {getHeaderGroups().map((headerGroup) => {
            const { headers } = headerGroup;

            const firstHeader = headers[0];

            return (
              <tr key={headerGroup.id}>
                <TableHead
                  className={clsx(
                    "sticky left-0 z-10 border-r border-black-150 bg-white",
                  )}
                >
                  <div className="flex gap-x-2.5 items-center">
                    <RenderIf condition={isRowSelectionEnabled}>
                      {isLoading ? (
                        <Skeleton className="size-4 rounded-[5px]" />
                      ) : (
                        <Checkbox
                          checked={selectAllCheckboxState}
                          aria-label="Select all"
                          onCheckedChange={() => {
                            toggleAllPageRowsSelected(!selectAllCheckboxState);
                          }}
                        />
                      )}
                    </RenderIf>
                    <div className="flex items-center gap-x-1">
                      <span>
                        {firstHeader.isPlaceholder
                          ? null
                          : flexRender(
                              firstHeader.column.columnDef.header,
                              firstHeader.getContext(),
                            )}
                      </span>

                      {(isRowSelectionEnabled ||
                        totalItemCount !== undefined) && (
                        <Tag variant="tertiary" size="sm">
                          {hasSelectedRows ? (
                            <span>
                              {selectedRowsCount}/
                              <span className="text-black-500">
                                {totalItemCount}
                              </span>
                            </span>
                          ) : (
                            <span className="text-black-500">
                              {formatNumber(totalItemCount)}
                            </span>
                          )}
                        </Tag>
                      )}
                      {selectionActionButton}
                    </div>
                  </div>
                </TableHead>

                {headers.slice(1).map((header, sliceIndex) => {
                  const MetaIcon = header.column.columnDef.meta?.icon;
                  return (
                    <TableHead
                      key={header.id}
                      className={clsx(
                        sliceIndex === 0 ? "border-l-0" : "border-l",
                        header.column.columnDef.meta?.isSticky &&
                          "last:sticky last:right-0 last:z-10 last:bg-white",
                        header.column.columnDef.meta?.className,
                        header.column.columnDef.meta?.headClassName,
                      )}
                    >
                      <div className="flex items-center gap-x-1 w-full">
                        {MetaIcon && <MetaIcon className="size-4" />}
                        <span className="truncate first-letter:uppercase">
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                        </span>
                      </div>
                    </TableHead>
                  );
                })}
              </tr>
            );
          })}
        </thead>

        <tbody>
          {getRowModel().rows.map(
            ({
              id,
              original,
              getIsSelected,
              toggleSelected,
              getVisibleCells,
            }) => {
              const visibleCells = getVisibleCells();

              return (
                <TableRow
                  key={id}
                  onClick={
                    canInteractWithRows
                      ? (e) => {
                          const target = e.target as HTMLElement;
                          const isInteractiveClick = target.closest(
                            'button, a, [role="menuitem"], input, label',
                          );

                          if (!isInteractiveClick) {
                            onRowClick(original);
                          }
                        }
                      : undefined
                  }
                  data-state={getIsSelected() ? "selected" : undefined}
                >
                  {visibleCells.map((cell, index) => {
                    const isFirstCell = index === 0;

                    return (
                      <TableCell
                        key={cell.id}
                        className={clsx(
                          "border-l overflow-hidden",
                          "first:sticky first:left-0 first:z-10 first:border-r first:border-black-150 first:bg-white first:border-l-0 first:min-w-72",
                          cell.column.columnDef.meta?.isSticky &&
                            "last:sticky last:right-0 last:z-10 last:bg-white",
                          index === 1 && "border-l-0",
                          canInteractWithRows &&
                            "group-hover/table-row:first:bg-purple-25 group-hover/table-row:last:bg-purple-25",
                          "group-data-[state=selected]/table-row:first:bg-purple-50 group-data-[state=selected]/table-row:last:bg-purple-50",
                          cell.column.columnDef.meta?.className,
                          cell.column.columnDef.meta?.bodyClassName,
                        )}
                      >
                        {isFirstCell && isRowSelectionEnabled ? (
                          <div className="flex items-center gap-x-2.5">
                            {isLoading ? (
                              <Skeleton className="size-4 rounded-[5px]" />
                            ) : (
                              <Checkbox
                                checked={getIsSelected()}
                                aria-label="Select row"
                                onClick={(e) => e.stopPropagation()}
                                onCheckedChange={toggleSelected}
                              />
                            )}

                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </div>
                        ) : (
                          flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            },
          )}
        </tbody>
      </Table>
      {isRowSelectionEnabled && (
        <div
          className={clsx(
            "sticky bottom-6 z-10 mx-auto w-fit h-13.5",
            getRowModel().rows.length === 1 ? "-mt-17 mb-3.5" : "-mt-19.5 mb-6",
          )}
        >
          {selectionActionsComponent}
        </div>
      )}
    </div>
  );
}
